<script>
import baseMixin from '@/mixins/baseMixin'
import CommentModel from '@/models/comments/commentModel'
import timesheetMixin from '@/mixins/timesheetMixin'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { mapGetters } from 'vuex'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'
import DisputeTimesheetDetailsCandidateCommentModel from '@/models/disputes/disputeTimesheetDetailsCandidateCommentModel'
import CommentBase from './comment-base.vue'
import CommentBaseText from './comment-base-text.vue'
import CommentDisputeHistoryActionBtn from './comment-dispute-history-action-btn.vue'

export default {
  name: 'CommentDisputeHistoryCandidate',
  components: { CommentBase, CommentBaseText, CommentDisputeHistoryActionBtn },
  mixins: [baseMixin, timesheetMixin],
  props: {
    comment: {
      type: CommentModel,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
    }),
    serialisedComment() {
      return new DisputeTimesheetDetailsCandidateCommentModel(
        JSON.parse(this.comment.commentText)
      )
    },
    isSelected() {
      return this.resolveStatus === ResolveOperations.CANDIDATE
    },
    compButtonText() {
      if (this.isSelected) return this.$t('general.selected')
      return this.$t('comment.disputeHistory.useThisBtnText')
    },
    compCommentTitle() {
      switch (this.serialisedComment.details.operationType) {
        case TimesheetOperationTypeInts.ACCEPT:
          return this.$t('comment.disputeHistory.candidate.title.noChanges')
        case TimesheetOperationTypeInts.MODIFY:
          return this.$t('comment.disputeHistory.candidate.title.changes')
        case TimesheetOperationTypeInts.CANCEL:
          return this.$t('comment.disputeHistory.candidate.title.cancel')
        default:
          return this.$t('comment.disputeHistory.candidate.title.default')
      }
    },
    hasModifedTimesheet() {
      return (
        this.serialisedComment.details.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    compStartTimeChange() {
      if (
        this.serialisedComment.details.previousStartTime ===
        this.serialisedComment.details.startTime
      )
        return null

      return `${this.renderLocalTime(
        this.serialisedComment.details.previousStartTime,
        DateFormatToken.HHmmss
      )} ➜ ${this.renderLocalTime(
        this.serialisedComment.details.startTime,
        DateFormatToken.HHmmss
      )}`
    },
    compEndTimeChange() {
      if (
        this.serialisedComment.details.previousEndTime ===
        this.serialisedComment.details.endTime
      )
        return null

      return `${this.renderLocalTime(
        this.serialisedComment.details.previousEndTime,
        DateFormatToken.HHmmss
      )} ➜ ${this.renderLocalTime(
        this.serialisedComment.details.endTime,
        DateFormatToken.HHmmss
      )}`
    },
    compBreakMinutesChange() {
      if (
        (this.serialisedComment.details.previousUnpaidBreakMinutes || 0) ===
        (this.serialisedComment.details.unpaidBreakMinutes || 0)
      )
        return null

      return `${
        this.serialisedComment.details.previousUnpaidBreakMinutes || 0
      } ➜ ${this.serialisedComment.details.unpaidBreakMinutes || 0} mins`
    },
    compReceivedRestBreaks() {
      if (!this.serialisedComment.shouldShowRestBreaksQuestion) return null

      return this.serialisedComment.details.isRestBreakCompliant
        ? this.$t('general.yes')
        : this.$t('general.no')
    },
  },
  methods: {
    updateResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: ResolveOperations.CANDIDATE,
      })
    },
  },
}
</script>

<template>
  <CommentBase
    :added-by="comment.addedBy"
    :subtitle="comment.getDateCreatedTimestamp()"
    :active="isSelected"
  >
    <CommentBaseText class="mb-1">{{ compCommentTitle }}</CommentBaseText>

    <div v-if="hasModifedTimesheet">
      <CommentBaseText v-if="compStartTimeChange"
        ><span class="font-weight-bold"
          >{{ $t('comment.disputeHistory.startTimeLabel') }}:</span
        >
        {{ compStartTimeChange }}</CommentBaseText
      >
      <CommentBaseText v-if="compEndTimeChange"
        ><span class="font-weight-bold"
          >{{ $t('comment.disputeHistory.endTimeLabel') }}:</span
        >
        {{ compEndTimeChange }}</CommentBaseText
      >
      <CommentBaseText v-if="compBreakMinutesChange"
        ><span class="font-weight-bold"
          >{{ $t('comment.disputeHistory.breakMinsLabel') }}:</span
        >
        {{ compBreakMinutesChange }}</CommentBaseText
      >

      <CommentBaseText v-if="compReceivedRestBreaks"
        ><span class="font-weight-bold"
          >{{ $t('comment.disputeHistory.restBreaksComplianceLabel') }}:</span
        >
        {{ compReceivedRestBreaks }}</CommentBaseText
      >
    </div>

    <template v-slot:actions>
      <CommentDisputeHistoryActionBtn
        :selected="isSelected"
        :text="compButtonText"
        @click="updateResolveStatus"
      />
    </template>
  </CommentBase>
</template>
